import {
  TeButtonMinimal,
  TeCallout,
  TeContainer,
  TeGrid,
  TeSpacing,
  TeText,
} from '@topcon/tuicl-react';
import styled from 'styled-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Section } from '../../utils/styling';
import Card from '../../components/Card';
import { AppRoutes } from '../../app/routes/utils';
import Gravatar from '../../components/ProfileImage';
import { useAppData } from '../../hooks/queries/appSession.queries';
import { useMigrationCandidateBusinessPartners } from '../../hooks/queries/migration.queries';
import featureFlags from '../../utils/featureFlags';

const CalloutWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 1280px;
`;

const CalloutAction = styled.div`
  position: absolute;
  right: 48px;
  top: 50%;
  transform: translateY(-50%);
`;

function MyOverview() {
  const { t } = useTranslation();
  const { identity, isIdentifiedThroughPing, pingIdentityBaseUrl } = useAppData();
  const { data: migrationCandidates } = useMigrationCandidateBusinessPartners();
  const navigate = useNavigate();
  const [isCalloutDismissed, setIsCalloutDismissed] = useState(false);

  const hasMigrationCandidates =
    featureFlags.MIGRATION &&
    !isCalloutDismissed &&
    migrationCandidates?.items &&
    migrationCandidates.items.length > 0;

  return (
    <TeContainer column grow padding="xs" gap="xs" overflow="auto" alignItems="center">
      <Section>
        <TeContainer fill column padding="xl" gap="xs" alignItems="center">
          <Gravatar email={identity?.email ?? ''} size={96} />
          <TeSpacing direction="y" size="xs" />
          <TeText size={20} weight={700}>
            {identity?.firstName} {identity?.lastName}
          </TeText>
          <TeText>{identity?.email}</TeText>
        </TeContainer>
      </Section>

      {hasMigrationCandidates && (
        <CalloutWrapper>
          <TeCallout
            status="info"
            icon="info"
            heading={t('suggestedOrganizations.heading')}
            text={t('suggestedOrganizations.description')}
            closeable
            teclose={() => setIsCalloutDismissed(true)}
            grow
            style={{ borderColor: '#005FAD' }}
          />
          <CalloutAction>
            <TeButtonMinimal
              text={t('suggestedOrganizations.reviewSuggestions')}
              size="s"
              teclick={() => navigate(AppRoutes.UnclaimedOrganizations)}
            />
          </CalloutAction>
        </CalloutWrapper>
      )}

      <Section>
        <TeGrid gap="s">
          {isIdentifiedThroughPing && pingIdentityBaseUrl && (
            <Card
              icon="manage_accounts"
              title={t('myProfile.title')}
              description={t('myProfile.description')}
              buttonText={t('myProfile.buttonText')}
              buttonIcon="open_in_new"
              route={{
                to: `${pingIdentityBaseUrl}/myaccount/#profile`,
                target: '_blank',
                rel: 'noopener noreferrer',
              }}
            />
          )}
          <Card
            icon="notifications"
            title={t('notifications.title')}
            description={t('notifications.description')}
            buttonText={t('notifications.buttonText')}
            route={{ to: AppRoutes.Notifications }}
          />
          <Card
            icon="domain"
            title={t('companies.title')}
            description={t('companies.description')}
            buttonText={t('companies.buttonText')}
            route={{ to: AppRoutes.Companies }}
          />
          <Card
            icon="settings"
            title={t('accountSettings.title')}
            description={t('accountSettings.description')}
            buttonText={t('accountSettings.buttonText')}
            route={{
              to: AppRoutes.Settings,
            }}
          />
        </TeGrid>
      </Section>
    </TeContainer>
  );
}

export default MyOverview;
